/**
 * `meta` document schema for the v2 section signup data model.
 *
 * One meta doc exists per course at
 * `course/{courseId}/sectionSignupsV2/meta`. It holds the
 * dynamic per-pool student targets, the auto-bump configuration
 * (including the hard ceiling, threshold, and amount for each
 * pool), and the auto-bump bookkeeping.
 *
 * # Invariant: current fields are set explicitly at write time
 *
 * **Every current field on the meta doc is written explicitly
 * by current writers.** Readers never substitute defaults for
 * missing per-pool fields in freshly-seeded docs. Defaults live
 * on the write side (seed callable) so the default logic is in
 * exactly one place per writer.
 *
 * Optional-feeling timestamps (`lastBumpedAt{Main,Minor}`) are
 * modeled as `FirestoreTimestampSchema.nullable()`: the field
 * key is required, but `null` is a valid value until the first
 * bump happens. The seed callable writes `null` explicitly.
 *
 * Similarly, `bumpEvents{Main,Minor}` are required arrays; the
 * seed writes `[]`.
 *
 * Legacy shared-rule fields (`bumpThreshold`, `bumpAmount`) are
 * kept optional for backwards compatibility with courses seeded
 * before the per-pool bump-rule migration. New writers omit
 * them and write `bumpThreshold{Main,Minor}` /
 * `bumpAmount{Main,Minor}` explicitly.
 *
 * SL section-time gate fields (`minStudentsPerSection*`,
 * `slSlotCaps*`, `configEvents`, ...) are the exception to the
 * explicit-at-write invariant: they arrived after courses were
 * already seeded, so they are optional and readers go through
 * `getSectionSignupV2PoolGateConfig`, which owns their defaults.
 */
import {z} from 'zod';

import {FirestoreTimestampSchema} from '../types';

/**
 * Defaults used by the seed callable. Readers never use these
 * constants — they exist so the seed has one place to look up
 * the initial values for new courses.
 */
export const SECTION_SIGNUP_V2_DEFAULT_MAX_STUDENTS_PER_SECTION_SLOT = 10;

/**
 * Default hard ceiling — auto-bump never grows
 * `maxStudentsPerSectionSlot{Main|Minor}` past this value. Set
 * at seed time and editable by admin from the dashboard.
 */
export const SECTION_SIGNUP_V2_DEFAULT_BUMP_CEILING = 20;

/**
 * Default number of full slots required to trigger an automatic
 * bump. Clamped at runtime to
 * `min(threshold, totalSlotsWithSectionsInPool)`.
 */
export const SECTION_SIGNUP_V2_DEFAULT_BUMP_THRESHOLD = 3;

/**
 * Default amount the dynamic target grows per automatic bump.
 * One is the smallest sane value — it gives admins time to
 * react and minimizes the risk of over-shooting.
 */
export const SECTION_SIGNUP_V2_DEFAULT_BUMP_AMOUNT = 1;

/** How many bump events per pool the meta doc keeps. */
export const SECTION_SIGNUP_V2_BUMP_EVENT_LOG_LIMIT = 20;

/**
 * Default smallest acceptable students per section, used to size
 * `slSlotCaps` and by the phase-2 join test in
 * `sectionSignupV2SlGate.ts`.
 */
export const SECTION_SIGNUP_V2_DEFAULT_MIN_STUDENTS_PER_SECTION = 5;

/**
 * Default fraction of `expectedStudents` picks after which the SL
 * gate's phase-2 join test applies.
 */
export const SECTION_SIGNUP_V2_DEFAULT_LIVE_MIN_AT = 0.3;

/**
 * How long after the course doc's `studentOnboardingStartDateTimestamp`
 * the SL gate waits before phase 2,
 * so students in every time zone have had a daytime to pick before live
 * counts can refuse SLs.
 */
export const SECTION_SIGNUP_V2_PHASE_TWO_HOLD_MS = 24 * 60 * 60 * 1000;

/** Default headroom multiplier applied when computing `slSlotCaps`. */
export const SECTION_SIGNUP_V2_DEFAULT_SAFETY = 1;

/** How many SL slot-cap events per pool the meta doc keeps. */
export const SECTION_SIGNUP_V2_SL_SLOT_CAP_EVENT_LOG_LIMIT = 50;

/** How many `configEvents` entries the meta doc keeps. */
export const SECTION_SIGNUP_V2_CONFIG_EVENT_LOG_LIMIT = 50;

/**
 * A single entry in the bump event log. Both auto and manual
 * bumps produce one of these; the dashboard renders them as a
 * timeline strip per pool.
 */
export const SectionSignupV2BumpEventSchema = z.object({
  at: FirestoreTimestampSchema,
  fromMaxStudentsPerSection: z.number().int().positive(),
  toMaxStudentsPerSection: z.number().int().positive(),
  reason: z.enum(['auto', 'manual']),
});

export type SectionSignupV2BumpEvent = z.infer<
  typeof SectionSignupV2BumpEventSchema
>;

/**
 * SL section caps for one pool, keyed by `String(timeIndex)`. A
 * slot with no key has no cap. See `sectionSignupV2SlGate.ts`
 * for how the gate reads it.
 */
export const SectionSignupV2SlSlotCapsSchema = z.record(
  z.string(),
  z.number().int().nonnegative(),
);

/** Per-pool SL section caps. See {@link SectionSignupV2SlSlotCapsSchema}. */
export type SectionSignupV2SlSlotCaps = z.infer<
  typeof SectionSignupV2SlSlotCapsSchema
>;

/** One admin change to a single slot's SL cap. */
export const SectionSignupV2SlSlotCapEventSchema = z.object({
  /** When the change was written. */
  at: FirestoreTimestampSchema,
  /** Slot whose cap changed. */
  timeIndex: z.number().int().nonnegative(),
  /** Cap before the change; `null` when the slot had no cap. */
  from: z.number().int().nonnegative().nullable(),
  /** Cap after the change; `null` when the cap was removed. */
  to: z.number().int().nonnegative().nullable(),
  /** Only admins change SL caps; nothing raises them automatically. */
  reason: z.literal('manual'),
  /** UID of the admin who made the change. */
  by: z.string(),
});

/** See {@link SectionSignupV2SlSlotCapEventSchema}. */
export type SectionSignupV2SlSlotCapEvent = z.infer<
  typeof SectionSignupV2SlSlotCapEventSchema
>;

/**
 * A value recorded in a `configEvents` entry: a scalar meta
 * field, a slot-cap map, or `null` when the field was unset.
 */
export const SectionSignupV2ConfigValueSchema = z.union([
  z.number(),
  z.string(),
  z.boolean(),
  z.null(),
  z.record(z.string(), z.number()),
]);

/** See {@link SectionSignupV2ConfigValueSchema}. */
export type SectionSignupV2ConfigValue = z.infer<
  typeof SectionSignupV2ConfigValueSchema
>;

/**
 * One logged change to the gate configuration or one staff
 * bypass of the gate.
 */
export const SectionSignupV2ConfigEventSchema = z.object({
  /** When the change was written. */
  at: FirestoreTimestampSchema,
  /** UID of the staff member who made the change. */
  by: z.string(),
  /**
   * Meta field name that changed, or a marker such as
   * `'adminOverride'` for a staff bypass of the SL gate.
   */
  field: z.string(),
  /** Value before the change. */
  from: SectionSignupV2ConfigValueSchema,
  /** Value after the change. */
  to: SectionSignupV2ConfigValueSchema,
  /**
   * Application and roster counts shown to the admin when the
   * change was saved, keyed by a label the admin page chooses.
   * Absent for entries not written from the config panel.
   */
  applicationCounts: z
    .record(z.string(), z.number().int().nonnegative())
    .optional(),
});

/** See {@link SectionSignupV2ConfigEventSchema}. */
export type SectionSignupV2ConfigEvent = z.infer<
  typeof SectionSignupV2ConfigEventSchema
>;

/**
 * The full `meta` document. New docs write every current field
 * explicitly; legacy shared bump-rule fields remain optional so
 * pre-migration docs can still be read.
 */
export const SectionSignupV2MetaSchema = z
  .object({
    courseId: z.string(),

    // Dynamic per-section targets (the value auto-bump grows).
    maxStudentsPerSectionSlotMain: z.number().int().positive(),
    maxStudentsPerSectionSlotMinor: z.number().int().positive(),

    // Per-pool hard ceilings — auto-bump never grows past these.
    bumpCeilingMain: z.number().int().positive(),
    bumpCeilingMinor: z.number().int().positive(),

    // Auto-bump rule configuration, one rule per pool.
    bumpThresholdMain: z.number().int().nonnegative().optional(),
    bumpThresholdMinor: z.number().int().nonnegative().optional(),
    bumpAmountMain: z.number().int().nonnegative().optional(),
    bumpAmountMinor: z.number().int().nonnegative().optional(),

    /**
     * Legacy shared bump rule, kept optional so pre-migration
     * docs still read successfully until they are re-seeded or
     * edited through the per-pool UI.
     */
    bumpThreshold: z.number().int().nonnegative().optional(),
    bumpAmount: z.number().int().nonnegative().optional(),

    // Auto-bump bookkeeping.
    autoBumpCountMain: z.number().int().nonnegative(),
    autoBumpCountMinor: z.number().int().nonnegative(),
    /**
     * When the last successful bump happened for this pool.
     * `null` until the first bump — the seed callable writes
     * `null` explicitly.
     */
    lastBumpedAtMain: FirestoreTimestampSchema.nullable(),
    lastBumpedAtMinor: FirestoreTimestampSchema.nullable(),

    /**
     * Last `SECTION_SIGNUP_V2_BUMP_EVENT_LOG_LIMIT` bump events
     * per pool, newest last. Writers append then trim. Seed
     * writes `[]`.
     */
    bumpEventsMain: z.array(SectionSignupV2BumpEventSchema),
    bumpEventsMinor: z.array(SectionSignupV2BumpEventSchema),

    // SL section-time gate configuration. Optional so docs seeded
    // before the gate still parse; read through
    // `getSectionSignupV2PoolGateConfig`, which applies defaults.

    /** Smallest acceptable students per section; sizes `slSlotCaps`. */
    minStudentsPerSectionMain: z.number().int().positive().optional(),
    minStudentsPerSectionMinor: z.number().int().positive().optional(),
    /** Largest acceptable students per section; the SL leave test. */
    maxStudentsPerSectionMain: z.number().int().positive().optional(),
    maxStudentsPerSectionMinor: z.number().int().positive().optional(),
    /**
     * Student time picks expected by the student onboarding close.
     * Absent means not configured, which keeps the SL gate in phase 1.
     */
    expectedStudentsMain: z.number().int().nonnegative().optional(),
    expectedStudentsMinor: z.number().int().nonnegative().optional(),
    /**
     * Fraction of `expectedStudents` picks, in `[0, 1]`, after
     * which the phase-2 join test applies.
     */
    liveMinAtMain: z.number().min(0).max(1).optional(),
    liveMinAtMinor: z.number().min(0).max(1).optional(),
    /** Per-slot SL section caps; see {@link SectionSignupV2SlSlotCapsSchema}. */
    slSlotCapsMain: SectionSignupV2SlSlotCapsSchema.optional(),
    slSlotCapsMinor: SectionSignupV2SlSlotCapsSchema.optional(),
    /**
     * Last `SECTION_SIGNUP_V2_SL_SLOT_CAP_EVENT_LOG_LIMIT` manual
     * slot-cap changes per pool, newest last.
     */
    slSlotCapEventsMain: z
      .array(SectionSignupV2SlSlotCapEventSchema)
      .optional(),
    slSlotCapEventsMinor: z
      .array(SectionSignupV2SlSlotCapEventSchema)
      .optional(),
    /** Course-level headroom multiplier used when computing `slSlotCaps`. */
    safety: z.number().positive().optional(),
    /**
     * Last `SECTION_SIGNUP_V2_CONFIG_EVENT_LOG_LIMIT` gate config
     * changes and staff bypasses, newest last.
     */
    configEvents: z.array(SectionSignupV2ConfigEventSchema).optional(),
  })
  .loose();

export type SectionSignupV2Meta = z.infer<typeof SectionSignupV2MetaSchema>;

/**
 * Resolved bump rule for one pool.
 */
export interface SectionSignupV2ResolvedPoolBumpRule {
  bumpCeiling: number;
  bumpThreshold: number;
  bumpAmount: number;
}

/**
 * Resolves the bump rule for one pool. Prefers the new per-pool
 * fields and falls back to the legacy shared fields for
 * pre-migration docs.
 */
export function getSectionSignupV2PoolBumpRule({
  meta,
  type,
}: {
  meta: SectionSignupV2Meta;
  type: 'main' | 'minor';
}): SectionSignupV2ResolvedPoolBumpRule {
  const legacyThreshold =
    meta.bumpThreshold ?? SECTION_SIGNUP_V2_DEFAULT_BUMP_THRESHOLD;
  const legacyAmount = meta.bumpAmount ?? SECTION_SIGNUP_V2_DEFAULT_BUMP_AMOUNT;

  if (type === 'main') {
    return {
      bumpCeiling: meta.bumpCeilingMain,
      bumpThreshold: meta.bumpThresholdMain ?? legacyThreshold,
      bumpAmount: meta.bumpAmountMain ?? legacyAmount,
    };
  }

  return {
    bumpCeiling: meta.bumpCeilingMinor,
    bumpThreshold: meta.bumpThresholdMinor ?? legacyThreshold,
    bumpAmount: meta.bumpAmountMinor ?? legacyAmount,
  };
}

/** SL gate configuration for one pool with defaults applied. */
export interface SectionSignupV2ResolvedPoolGateConfig {
  /** `maxStudentsPerSectionSlot{Main,Minor}`: seats per section. */
  capStudents: number;
  /** Smallest acceptable students per section. */
  minStudentsPerSection: number;
  /**
   * Largest acceptable students per section, used by the leave
   * test. Falls back to the pool's `bumpCeiling`, the largest
   * section size the course already accepts.
   */
  maxStudentsPerSection: number;
  /**
   * Expected student picks, or `null` when the meta doc does not set
   * one (not configured). A configured value, `0` included, is a
   * number.
   */
  expectedStudents: number | null;
  /** Fraction of `expectedStudents` at which phase 2 starts. */
  liveMinAt: number;
  /**
   * Earliest time phase 2 may start, in epoch ms:
   * the course doc's `studentOnboardingStartDateTimestamp` plus
   * `SECTION_SIGNUP_V2_PHASE_TWO_HOLD_MS`, or `null` when it is absent,
   * empty, or not a parseable date.
   */
  phaseTwoNotBeforeMs: number | null;
  /** Per-slot SL caps keyed by `String(timeIndex)`; `{}` when unset. */
  slSlotCaps: Readonly<SectionSignupV2SlSlotCaps>;
  /** Course-level headroom multiplier. */
  safety: number;
}

/**
 * Resolves the SL gate configuration for one pool, applying the
 * `SECTION_SIGNUP_V2_DEFAULT_*` constants to fields a pre-gate
 * meta doc does not have. `expectedStudents` has no default: an
 * absent field resolves to `null` (not configured).
 *
 * @param options.meta - The parsed meta doc.
 * @param options.type - Which pool to resolve.
 * @param options.studentOnboardingStartDateTimestamp - The course doc's
 *   `studentOnboardingStartDateTimestamp` (ISO 8601 with UTC offset), or
 *   `undefined` when the course has none. Absent, empty, or unparseable
 *   resolves `phaseTwoNotBeforeMs` to `null`.
 * @return Concrete gate numbers for the pool.
 * @throws Never.
 */
export function getSectionSignupV2PoolGateConfig({
  meta,
  type,
  studentOnboardingStartDateTimestamp,
}: {
  meta: SectionSignupV2Meta;
  type: 'main' | 'minor';
  studentOnboardingStartDateTimestamp: string | undefined;
}): SectionSignupV2ResolvedPoolGateConfig {
  const safety = meta.safety ?? SECTION_SIGNUP_V2_DEFAULT_SAFETY;
  const startMs = Date.parse(studentOnboardingStartDateTimestamp ?? '');
  const phaseTwoNotBeforeMs = Number.isNaN(startMs)
    ? null
    : startMs + SECTION_SIGNUP_V2_PHASE_TWO_HOLD_MS;
  if (type === 'main') {
    return {
      capStudents: meta.maxStudentsPerSectionSlotMain,
      minStudentsPerSection:
        meta.minStudentsPerSectionMain ??
        SECTION_SIGNUP_V2_DEFAULT_MIN_STUDENTS_PER_SECTION,
      maxStudentsPerSection:
        meta.maxStudentsPerSectionMain ?? meta.bumpCeilingMain,
      expectedStudents: meta.expectedStudentsMain ?? null,
      liveMinAt: meta.liveMinAtMain ?? SECTION_SIGNUP_V2_DEFAULT_LIVE_MIN_AT,
      phaseTwoNotBeforeMs,
      slSlotCaps: meta.slSlotCapsMain ?? {},
      safety,
    };
  }

  return {
    capStudents: meta.maxStudentsPerSectionSlotMinor,
    minStudentsPerSection:
      meta.minStudentsPerSectionMinor ??
      SECTION_SIGNUP_V2_DEFAULT_MIN_STUDENTS_PER_SECTION,
    maxStudentsPerSection:
      meta.maxStudentsPerSectionMinor ?? meta.bumpCeilingMinor,
    expectedStudents: meta.expectedStudentsMinor ?? null,
    liveMinAt: meta.liveMinAtMinor ?? SECTION_SIGNUP_V2_DEFAULT_LIVE_MIN_AT,
    phaseTwoNotBeforeMs,
    slSlotCaps: meta.slSlotCapsMinor ?? {},
    safety,
  };
}
